import { Suspense } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Card, CardTitle } from "@/core/ui";
import { getPuzzleType } from "@/puzzle-types/registry";
import { usePuzzlePreview } from "./api";
import { PuzzleCreditLine } from "./Credit";

/**
 * Read-only rendering of the latest definition, for authors / reviewers / admins.
 * Uses the registry only: the core never imports a concrete puzzle type.
 */
export function PuzzlePreviewPage() {
  const { slug = "" } = useParams();
  const { t, i18n } = useTranslation();
  const preview = usePuzzlePreview(slug, true);
  if (preview.isPending) return <p className="bt-page-message">{t("app.loading")}</p>;
  if (preview.isError || !preview.data)
    return <Alert tone="danger">{t("app.error_generic")}</Alert>;
  const { puzzle, version, credit } = preview.data;
  const module = getPuzzleType(puzzle.puzzle_type);
  if (!version || !module) return <Alert tone="danger">{t("puzzles.no_version")}</Alert>;
  const Player = module.Player;
  const definition = version.definition;
  const progress = module.emptyProgress(definition);
  const rules = extractRules(definition, i18n.language);
  return (
    <section className="bt-preview">
      <Card>
        <CardTitle>
          {puzzle.title} <span className="bt-preview__version">v{version.number}</span>
        </CardTitle>
        <PuzzleCreditLine credit={credit} />
        <Suspense fallback={<p className="bt-page-message">{t("app.loading")}</p>}>
          <Player
            definition={definition}
            progress={progress}
            onProgressChange={() => undefined}
            readOnly
            liveFeedback={false}
            locale={i18n.language}
          />
        </Suspense>
      </Card>
      <Card>
        <CardTitle>{t("play.rules")}</CardTitle>
        <RulesText text={rules} />
      </Card>
    </section>
  );
}

export function extractRules(definition: unknown, locale: string): string {
  if (typeof definition !== "object" || definition === null) return "";
  const rules = (definition as { rulesText?: Record<string, string> }).rulesText;
  if (!rules) return "";
  return (
    rules[locale] ||
    rules[locale.split("-")[0]] ||
    rules.fr ||
    rules.en ||
    Object.values(rules)[0] ||
    ""
  );
}

/** Minimal markdown: paragraphs and **bold**. `paper` sets it in the ink of the PDF sheet. */
export function RulesText({ text, paper = false }: { text: string; paper?: boolean }) {
  const paragraphs = text.split(/\n+/).filter(Boolean);
  return (
    <div className={paper ? "bt-rules bt-rules--paper" : "bt-rules"}>
      {paragraphs.map((p, i) => (
        <p key={i}>
          {p
            .split(/(\*\*[^*]+\*\*)/)
            .map((part, j) =>
              part.startsWith("**") && part.endsWith("**") ? (
                <strong key={j}>{part.slice(2, -2)}</strong>
              ) : (
                <span key={j}>{part}</span>
              ),
            )}
        </p>
      ))}
    </div>
  );
}
